import { tenantStorageKey } from './tenantStorage.ts'

/**
 * Uygulama içi bildirim kartına eşlik eden kısa ses.
 *
 * Sesler ek dosya indirilmeden Web Audio ile üretilir: PWA önbelleği,
 * APK boyutu ve çevrimdışı davranış değişmez. Tercih cihaz ve aktif
 * çalışma alanı başına saklanır; `off` seçildiğinde hiç ses çalınmaz.
 */

export type NotificationSoundId = 'off' | 'chime' | 'ping' | 'pop' | 'alert'

export const NOTIFICATION_SOUND_KEY = 'servernoxNotificationSound'
export const DEFAULT_NOTIFICATION_SOUND: NotificationSoundId = 'chime'

export const NOTIFICATION_SOUNDS: Array<{ id: NotificationSoundId; label: string; description: string }> = [
  { id: 'chime', label: 'Çan (önerilen)', description: 'İki notalı yumuşak çan; AI yanıtı ve iş sonuçları için dengeli.' },
  { id: 'ping', label: 'Ping', description: 'Tek ve kısa; sık bildirim alanlarda dikkat dağıtmaz.' },
  { id: 'pop', label: 'Pop', description: 'Alçak ve tok; sessiz ortamda en az duyulan seçenek.' },
  { id: 'alert', label: 'Uyarı', description: 'Üç notalı belirgin uyarı; kaçırılmaması gereken bildirimler için.' },
  { id: 'off', label: 'Sessiz', description: 'Uygulama içi bildirimlerde hiç ses çalınmaz.' },
]

type Note = { at: number; frequency: number; duration: number; peak: number; type: OscillatorType }

const RECIPES: Record<Exclude<NotificationSoundId, 'off'>, Note[]> = {
  chime: [
    { at: 0, frequency: 880, duration: 0.28, peak: 0.10, type: 'sine' },
    { at: 0.11, frequency: 1318.51, duration: 0.34, peak: 0.085, type: 'sine' },
  ],
  ping: [
    { at: 0, frequency: 1174.66, duration: 0.2, peak: 0.10, type: 'sine' },
  ],
  pop: [
    { at: 0, frequency: 392, duration: 0.09, peak: 0.11, type: 'triangle' },
    { at: 0.06, frequency: 587.33, duration: 0.14, peak: 0.08, type: 'triangle' },
  ],
  alert: [
    { at: 0, frequency: 659.25, duration: 0.11, peak: 0.10, type: 'triangle' },
    { at: 0.13, frequency: 987.77, duration: 0.11, peak: 0.10, type: 'triangle' },
    { at: 0.26, frequency: 659.25, duration: 0.22, peak: 0.09, type: 'triangle' },
  ],
}

/** Aynı anda gelen birden çok bildirimin sesi üst üste binmesin. */
const MIN_GAP_MS = 700
let lastPlayedAt = 0
let context: AudioContext | null = null

function validSound(value: unknown): value is NotificationSoundId {
  return typeof value === 'string' && (value === 'off' || value in RECIPES)
}

export function getNotificationSound(storage: Pick<Storage, 'getItem'> = localStorage): NotificationSoundId {
  try {
    const value = storage.getItem(tenantStorageKey(NOTIFICATION_SOUND_KEY, storage))
    return validSound(value) ? value : DEFAULT_NOTIFICATION_SOUND
  } catch {
    return DEFAULT_NOTIFICATION_SOUND
  }
}

export function saveNotificationSound(sound: NotificationSoundId, storage: Storage = localStorage): void {
  const normalized = validSound(sound) ? sound : DEFAULT_NOTIFICATION_SOUND
  try { storage.setItem(tenantStorageKey(NOTIFICATION_SOUND_KEY, storage), normalized) } catch {}
}

function audioContext(): AudioContext | null {
  const Ctor = typeof window === 'undefined'
    ? undefined
    : window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  try {
    if (!context) context = new Ctor()
    // Tarayıcı ve WebView, kullanıcı etkileşimi öncesi bağlamı askıya alır.
    if (context.state === 'suspended') void context.resume()
    return context
  } catch {
    return null
  }
}

/**
 * Seçili sesi çalar. Ses cihazı yoksa, otomatik oynatma engellendiyse veya
 * kullanıcı `Sessiz` seçtiyse sessizce hiçbir şey yapmaz; bildirim kartının
 * gösterimi hiçbir koşulda ses hatasına bağlı değildir.
 */
export function playNotificationSound(
  sound: NotificationSoundId = getNotificationSound(),
  options: { force?: boolean } = {},
): boolean {
  if (sound === 'off') return false
  const recipe = RECIPES[sound]
  if (!recipe) return false
  const now = Date.now()
  if (!options.force && now - lastPlayedAt < MIN_GAP_MS) return false
  const ctx = audioContext()
  if (!ctx) return false
  try {
    const start = ctx.currentTime + 0.01
    for (const note of recipe) {
      const oscillator = ctx.createOscillator()
      const gain = ctx.createGain()
      oscillator.type = note.type
      oscillator.frequency.setValueAtTime(note.frequency, start + note.at)
      gain.gain.setValueAtTime(0.0001, start + note.at)
      gain.gain.exponentialRampToValueAtTime(note.peak, start + note.at + 0.012)
      gain.gain.exponentialRampToValueAtTime(0.0001, start + note.at + note.duration)
      oscillator.connect(gain)
      gain.connect(ctx.destination)
      oscillator.start(start + note.at)
      oscillator.stop(start + note.at + note.duration + 0.02)
    }
    lastPlayedAt = now
    return true
  } catch {
    return false
  }
}

/** Ayarlar ekranındaki "Dinle" düğmesi gecikme kuralına takılmamalıdır. */
export function previewNotificationSound(sound: NotificationSoundId): boolean {
  return playNotificationSound(sound, { force: true })
}
